import { api, ApiError } from '../api.js'

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const sections = new Set(['Pumps', 'Probes', 'PriceBoards', 'Readers', 'Sensors', 'Tanks', 'FuelGrades',
  'PumpNozzles', 'MeasurementUnits', 'ControllerType', 'UsersConfiguration', 'RemoteServerConfiguration',
  'PtsNetworkSettings', 'TagsList', 'ReadersConfiguration'])
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const invalidResponse = () => new ApiError('Invalid PTS-2 configuration history response', 502, 'pts2_history_response_invalid')
const invalidQuery = () => new ApiError('Invalid PTS-2 configuration history query', 400, 'pts2_history_query_invalid')

function positiveId(value) {
  value = Number(value)
  if (!Number.isSafeInteger(value) || value < 1) throw invalidQuery()
  return value
}

function safeDate(value, nullable = false) {
  if (nullable && value === null) return null
  if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) throw invalidResponse()
  return value
}

function projectObservation(value, stationId, controllerId) {
  if (!isRecord(value) || !uuid.test(value.id) || value.stationId !== stationId || value.controllerId !== controllerId
    || !Number.isSafeInteger(value.sequence) || value.sequence < 1 || typeof value.configurationId !== 'string'
    || !['CONTROLLER_OBSERVED'].includes(value.source) || !isRecord(value.completeness)
    || !['BASELINE', 'AVAILABLE', 'PREVIOUS_UNAVAILABLE'].includes(value.diffState)
    || !(value.changeCount === null || Number.isSafeInteger(value.changeCount) && value.changeCount >= 0)) throw invalidResponse()
  return {
    id: value.id,
    sequence: value.sequence,
    stationId,
    controllerId,
    configurationId: value.configurationId,
    observedAt: safeDate(value.observedAt),
    deviceChangedAt: safeDate(value.deviceChangedAt, true),
    source: value.source,
    ordering: value.ordering,
    receiptKind: value.receiptKind,
    completeness: structuredClone(value.completeness),
    previousObservationId: value.previousObservationId,
    operationId: null,
    actor: null,
    diffState: value.diffState,
    changeCount: value.changeCount,
  }
}

function projectDifference(value) {
  if (!isRecord(value) || typeof value.section !== 'string' || !(typeof value.deviceId === 'string' || value.deviceId === null)
    || typeof value.field !== 'string' || !['ADDED', 'REMOVED', 'CHANGED', 'UNKNOWN'].includes(value.kind)
    || typeof value.redacted !== 'boolean' || !['CONTROLLER_OBSERVED', 'SERVER_OPERATION_VERIFIED'].includes(value.source)) throw invalidResponse()
  if (value.source === 'SERVER_OPERATION_VERIFIED' && (!uuid.test(value.operationId) || !isRecord(value.actor)
    || !Number.isSafeInteger(value.actor.userId) || typeof value.actor.username !== 'string')) throw invalidResponse()
  if (value.source === 'CONTROLLER_OBSERVED' && (value.operationId !== null || value.actor !== null)) throw invalidResponse()
  return structuredClone(value)
}

function projectDifferencePage(value) {
  if (!isRecord(value) || !Array.isArray(value.items) || value.items.length > 200
    || typeof value.baseline !== 'boolean' || !Number.isSafeInteger(value.total) || value.total < 0
    || typeof value.hasMore !== 'boolean' || !(value.nextCursor === null || typeof value.nextCursor === 'string')) throw invalidResponse()
  return { ...value, items: value.items.map(projectDifference) }
}

function queryFor(stationId, options = {}) {
  const query = new URLSearchParams({ stationId: String(positiveId(stationId)) })
  const { from, to, section, cursor, limit = 50 } = options
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 200) throw invalidQuery()
  query.set('limit', String(limit))
  if (from) query.set('from', new Date(from).toISOString())
  if (to) query.set('to', new Date(to).toISOString())
  if (section) {
    if (!sections.has(section)) throw invalidQuery()
    query.set('section', section)
  }
  if (cursor) query.set('cursor', cursor)
  return query
}

export async function listConfigurationHistory(stationId, controllerId, options = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  const payload = await api(`/pts2/controllers/${controllerId}/configuration-history?${queryFor(stationId, options)}`, {
    method: 'GET', signal: options.signal,
  })
  if (!isRecord(payload) || !Array.isArray(payload.items) || payload.items.length > (options.limit ?? 50)
    || typeof payload.hasMore !== 'boolean' || !(payload.nextCursor === null || typeof payload.nextCursor === 'string')) throw invalidResponse()
  return { items: payload.items.map((item) => projectObservation(item, stationId, controllerId)),
    nextCursor: payload.nextCursor, hasMore: payload.hasMore }
}

export async function getConfigurationObservation(stationId, controllerId, observationId, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (!uuid.test(observationId)) throw invalidQuery()
  const payload = await api(`/pts2/controllers/${controllerId}/configuration-history/${observationId}?stationId=${stationId}`, {
    method: 'GET', signal,
  })
  const observation = projectObservation(payload, stationId, controllerId)
  if (!isRecord(payload.configuration) || !(payload.diff === null || isRecord(payload.diff))) throw invalidResponse()
  return { ...observation, configuration: structuredClone(payload.configuration),
    diff: payload.diff === null ? null : projectDifferencePage(payload.diff) }
}

export async function compareConfigurationObservations(stationId, controllerId, fromObservationId,
  toObservationId, { cursor, limit = 200, signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (!uuid.test(fromObservationId) || !uuid.test(toObservationId) || !Number.isSafeInteger(limit)
    || limit < 1 || limit > 200) throw invalidQuery()
  const query = new URLSearchParams({ stationId: String(stationId), fromObservationId, toObservationId,
    limit: String(limit) })
  if (cursor) query.set('cursor', cursor)
  return projectDifferencePage(await api(`/pts2/controllers/${controllerId}/configuration-diff?${query}`, {
    method: 'GET', signal,
  }))
}
